// Legacy-AICOS shell: the macro module grid ("select a module") and the per-
// module WorkCenter sub-home (dense cards with title+description links). Visual
// parity with the legacy site/index.html; implemented links route to the real Go
// pages, the rest to /soon (legacy-parity scope, not yet wired in the rewrite).
//
// NOTE: the GL hub labels/descriptions below are inline English literals for the
// rapid visual build — t() returns the literal for any non-keyed string. i18n
// keying (en/fr/es) is a pending step before commit (ADR-LOCALE-001).
import { Link, NavLink, useParams, useSearchParams } from "react-router-dom";
import { t } from "../i18n";

type IconName =
  | "gl" | "ap" | "ar" | "bi" | "po" | "ex" | "ceo" | "cfo" | "tax" | "compliance" | "entities"
  | "doc" | "chart" | "lock" | "config" | "recon" | "coins" | "layers" | "search" | "shield" | "prm";

// Inline SVGs (stroke=currentColor) mirroring the legacy module/card glyphs.
const PATHS: Record<IconName, JSX.Element> = {
  gl: <><path d="M3 3v18h18" /><path d="M7 14l4-4 4 4 5-5" /></>,
  chart: <><path d="M4 20V10M10 20V4M16 20v-7M21 20H3" /></>,
  ap: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></>,
  ar: <><path d="M12 2v20" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></>,
  bi: <><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9 13h6M9 17h4" /></>,
  doc: <><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9 13h6M9 17h4" /></>,
  po: <><circle cx="9" cy="20" r="1" /><circle cx="18" cy="20" r="1" /><path d="M2 3h3l2.4 12.4a1 1 0 0 0 1 .8h8.8a1 1 0 0 0 1-.8L21 7H6" /></>,
  ex: <><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></>,
  ceo: <><path d="M3 7l4 5 5-7 5 7 4-5v11H3z" /></>,
  cfo: <><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /></>,
  tax: <><path d="M4 3h16v18l-3-2-3 2-3-2-3 2-3-2z" /><path d="M9 8h6M9 12h6" /></>,
  compliance: <><path d="M12 2l8 3v6c0 5-3.5 8-8 11-4.5-3-8-6-8-11V5z" /><path d="M9 12l2 2 4-4" /></>,
  entities: <><ellipse cx="12" cy="5" rx="8" ry="3" /><path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" /><path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" /></>,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  config: <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  recon: <><path d="M3 7h13l-3-3M21 17H8l3 3" /></>,
  coins: <><ellipse cx="8" cy="6" rx="5" ry="2.5" /><path d="M3 6v5c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5V6" /><path d="M11 13.5c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5V11c0-1.4-2.2-2.5-5-2.5" /></>,
  layers: <><path d="M12 2l9 5-9 5-9-5 9-5z" /><path d="M3 12l9 5 9-5M3 17l9 5 9-5" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" /></>,
  shield: <><path d="M12 2l8 3v6c0 5-3.5 8-8 11-4.5-3-8-6-8-11V5z" /></>,
  prm: <><path d="M5 20V5l7 8 7-8v15" /></>,
};

function Icon({ name }: { name: IconName }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {PATHS[name]}
    </svg>
  );
}

// Official Lopettia logomark (corp/branding): a 440×440 sunburst of 36 angular
// positions × 2 concentric bands (inner 100→162, outer 174→200, stroke 9, round
// caps) plus the curved-L letterform, all in the fixed Sunrise→Peach→Coral
// gradient. Rendered inline (cache-proof) and generated from the spec geometry.
export function Logo({ size = 38 }: { size?: number }) {
  const dashes: [number, number, number, number][] = [];
  for (let i = 0; i < 36; i++) {
    const a = ((i * 10 - 90) * Math.PI) / 180;
    const c = Math.cos(a), s = Math.sin(a);
    dashes.push([220 + 100 * c, 220 + 100 * s, 220 + 162 * c, 220 + 162 * s]); // inner band
    dashes.push([220 + 174 * c, 220 + 174 * s, 220 + 200 * c, 220 + 200 * s]); // outer stub
  }
  return (
    <svg width={size} height={size} viewBox="0 0 440 440" role="img" aria-label="Lopettia">
      <defs>
        <linearGradient id="lop-sunburst" x1="220" y1="20" x2="220" y2="420" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FCE28A" /><stop offset="50%" stopColor="#F7B285" /><stop offset="100%" stopColor="#F38281" />
        </linearGradient>
      </defs>
      {dashes.map((d, i) => (
        <line key={i} x1={d[0]} y1={d[1]} x2={d[2]} y2={d[3]} stroke="url(#lop-sunburst)" strokeWidth={9} strokeLinecap="round" />
      ))}
      <path d="M 198.5 177.5 A 5.5 5.5 0 0 0 187.5 177.5 L 187.5 265.4 Q 187.5 272 194.1 272 L 247 272 A 5.5 5.5 0 0 0 247 261 L 203.45 261 Q 198.5 261 198.5 256.05 L 198.5 177.5 Z" fill="url(#lop-sunburst)" />
    </svg>
  );
}

interface Mod { code: string; cls: string; nameKey: string; icon: IconName; to: string }

// Macro grid order = legacy (GL AP AR BI PO EX CEO CFO) + our net-new surfaces.
export const MODULES: Mod[] = [
  { code: "GL", cls: "m-gl", nameKey: "nav.gl", icon: "gl", to: "/m/GL" },
  { code: "AP", cls: "m-ap", nameKey: "mod.ap", icon: "ap", to: "/m/AP" },
  { code: "AR", cls: "m-ar", nameKey: "mod.ar", icon: "ar", to: "/m/AR" },
  { code: "BI", cls: "m-bi", nameKey: "nav.bi", icon: "bi", to: "/m/BI" },
  { code: "PO", cls: "m-po", nameKey: "nav.po", icon: "po", to: "/m/PO" },
  { code: "EX", cls: "m-ex", nameKey: "mod.ex", icon: "ex", to: "/m/EX" },
  { code: "CEO", cls: "m-ceo", nameKey: "nav.ceo", icon: "ceo", to: "/ceo" },
  { code: "CFO", cls: "m-cfo", nameKey: "nav.cfo", icon: "cfo", to: "/cfo" },
  { code: "TAX", cls: "m-tax", nameKey: "nav.tax", icon: "tax", to: "/tax" },
  { code: "COMP", cls: "m-compliance", nameKey: "nav.compliance", icon: "compliance", to: "/compliance" },
  { code: "ENT", cls: "m-entities", nameKey: "nav.entities", icon: "entities", to: "/entities" },
];

// Header module pills (legacy: GL AP AR BI PO EX CEO CFO).
export const PILLS = MODULES.filter((m) => ["GL", "AP", "AR", "BI", "PO", "EX", "CEO", "CFO"].includes(m.code));

interface HubLink { name: string; desc: string; to: string }
interface HubCard { titleKey: string; icon: IconName; links: HubLink[] }
interface Hub { cls: string; nameKey: string; sub?: string; cards: HubCard[] }

// WorkCenter sub-homes. The GL hub mirrors the legacy IA card-for-card.
const HUBS: Record<string, Hub> = {
  GL: {
    cls: "m-gl", nameKey: "nav.gl", sub: "Multi-ledger chart of accounts, journals with approval, multicurrency, consolidation and close.",
    cards: [
      { titleKey: "Configuration", icon: "config", links: [
        { name: "Business Units", desc: "Create and maintain Business Units", to: "/soon" },
        { name: "Accounting Ledgers", desc: "Define ledgers and ledger groups", to: "/soon" },
        { name: "Chart of Accounts", desc: "Create and maintain accounts", to: "/entities" },
        { name: "ChartFields", desc: "Accounting dimensions configuration", to: "/soon" },
        { name: "Fiscal Calendars", desc: "Accounting periods definition", to: "/soon" },
        { name: "Exchange Rates", desc: "Exchange rate tables and revaluation", to: "/soon" },
        { name: "Journal Sources", desc: "Journal Source definitions", to: "/soon" },
        { name: "Journal Classes", desc: "Accounting journal types and classes", to: "/soon" },
        { name: "Approval Options", desc: "Journal authorization workflow", to: "/soon" },
      ] },
      { titleKey: "Journals", icon: "doc", links: [
        { name: "Journal Entry", desc: "Create and edit manual journals", to: "/gl?t=entry" },
        { name: "Standard Journals", desc: "Recurring journals and templates", to: "/soon" },
        { name: "Import Journals", desc: "Bulk load from flat file or Excel", to: "/soon" },
        { name: "Edit Journals", desc: "Validation process (Edit)", to: "/gl?t=journals" },
        { name: "Post Journals", desc: "Posting process", to: "/gl?t=journals" },
        { name: "Unpost Journals", desc: "Posting reversal", to: "/gl?t=journals" },
        { name: "Approve Journals", desc: "Approval inbox", to: "/gl?t=journals" },
        { name: "Journal Inquiry", desc: "Search and inquire journals", to: "/gl?t=journals" },
      ] },
      { titleKey: "Reconciliation", icon: "recon", links: [
        { name: "Configure Rules", desc: "Account reconciliation rules", to: "/soon" },
        { name: "Reconciliation Workbench", desc: "Interactive reconciliation management", to: "/soon" },
        { name: "Create Reconciliations", desc: "Generate reconciliations", to: "/soon" },
      ] },
      { titleKey: "Multi-currency", icon: "coins", links: [
        { name: "Revaluation", desc: "FX balance revaluation process", to: "/soon" },
        { name: "Translation", desc: "Ledger translation process", to: "/soon" },
        { name: "Average Balances", desc: "ADB (Average Daily Balance) calculation", to: "/soon" },
      ] },
      { titleKey: "Consolidation", icon: "layers", links: [
        { name: "Configure Consolidation", desc: "Trees, scopes, eliminations", to: "/soon" },
        { name: "Execute Consolidation", desc: "Consolidation process", to: "/soon" },
        { name: "Consolidation Dashboard", desc: "Process monitoring", to: "/soon" },
      ] },
      { titleKey: "Closing", icon: "lock", links: [
        { name: "Closing Rules", desc: "Closing rules configuration", to: "/soon" },
        { name: "Interim Close", desc: "Monthly period close", to: "/soon" },
        { name: "Year-End Close", desc: "Fiscal year-end close", to: "/gl?t=yearend" },
        { name: "Undo Close", desc: "Close reversal", to: "/soon" },
      ] },
      { titleKey: "Reports", icon: "chart", links: [
        { name: "Balance Sheet", desc: "Statement of Financial Position", to: "/gl?t=reports" },
        { name: "Income Statement", desc: "Profit & Loss report", to: "/gl?t=reports" },
        { name: "Cash Flow", desc: "Statement of Cash Flows", to: "/soon" },
        { name: "Trial Balance", desc: "General Trial Balance", to: "/gl?t=balances" },
        { name: "General Ledger", desc: "Account movement detail", to: "/gl?t=balances" },
        { name: "Accounting Journals", desc: "Journal listing and detail", to: "/gl?t=journals" },
      ] },
      { titleKey: "Inquiries", icon: "search", links: [
        { name: "Ledger Inquiry", desc: "Interactive balances by dimension", to: "/gl?t=balances" },
        { name: "Journal Inquiry", desc: "Posted journal detail", to: "/gl?t=journals" },
        { name: "Period Comparison", desc: "Period-over-period balance comparison", to: "/soon" },
      ] },
      { titleKey: "Puerto Rico", icon: "prm", links: [
        { name: "PR Compliance Hub", desc: "IVU · 480 · License · CRIM · Act 60", to: "/compliance" },
        { name: "SC 2918 — Monthly IVU", desc: "Monthly sales-tax return to Hacienda", to: "/compliance" },
        { name: "Form 480.20 Corp", desc: "Income tax + surtax + AMT", to: "/compliance" },
        { name: "Form 480.6 A/B/C", desc: "Annual withholding informational returns", to: "/compliance" },
        { name: "Municipal License", desc: "Form 2A annual per municipality", to: "/compliance" },
        { name: "CRIM AS-29", desc: "Personal and real property", to: "/compliance" },
        { name: "Schedule M-1", desc: "Book/tax reconciliation", to: "/compliance" },
        { name: "Act 60 Decrees", desc: "Compliance + annual fee tracking", to: "/compliance" },
        { name: "Filings Audit Trail", desc: "Tamper-evident history", to: "/compliance" },
      ] },
      { titleKey: "Administration", icon: "shield", links: [
        { name: "Open/Close Periods", desc: "Period opening control", to: "/soon" },
        { name: "Process Monitor", desc: "Batch process status", to: "/soon" },
        { name: "Audit Logs", desc: "Change and access history", to: "/soon" },
      ] },
    ],
  },
  AP: {
    cls: "m-ap", nameKey: "nav.ap",
    sub: "Vendors, vouchers with 3-way match, pay cycles, withholding (PR Sec. 1062 + Form 480 + US 1099). Automatic GL integration.",
    cards: [
      { titleKey: "Configuration", icon: "config", links: [
        { name: "AP Business Units", desc: "AP BU setup and maintenance", to: "/soon" },
        { name: "AP Options by SetID", desc: "Global voucher and payment settings", to: "/soon" },
        { name: "Voucher Origins", desc: "Invoice entry types", to: "/soon" },
        { name: "Control Groups", desc: "Voucher group containers", to: "/soon" },
        { name: "Payment Formats", desc: "Check, EFT, wire templates", to: "/soon" },
        { name: "Pay Cycles", desc: "Pay Cycle parameter definition", to: "/soon" },
        { name: "Payment Terms", desc: "Payment condition setup", to: "/soon" },
        { name: "Withholding", desc: "Withholding codes and rates (PR Sec 1062 · 480)", to: "/soon" },
      ] },
      { titleKey: "Vendors", icon: "entities", links: [
        { name: "Add Vendor", desc: "Full vendor master registration", to: "/entities" },
        { name: "Vendor Maintenance", desc: "Edit banking, terms, contacts", to: "/entities" },
        { name: "Vendor Approval", desc: "New vendor approval flow", to: "/soon" },
        { name: "Vendor Inquiry", desc: "Search and view vendor information", to: "/entities" },
      ] },
      { titleKey: "Vouchers", icon: "doc", links: [
        { name: "Voucher Entry", desc: "Manual vendor invoice entry · live PR withholding", to: "/ap" },
        { name: "Quick Voucher", desc: "Simplified Express Entry", to: "/soon" },
        { name: "Bulk Load", desc: "Import invoices from interface/file", to: "/soon" },
        { name: "Matching", desc: "PO-Receipt-Invoice match process", to: "/soon" },
        { name: "Match Exceptions", desc: "Discrepancy review and resolution", to: "/soon" },
        { name: "Voucher Approval", desc: "Invoice authorization queue", to: "/soon" },
        { name: "Voucher Inquiry", desc: "Search and detail of vouchers", to: "/ap" },
      ] },
      { titleKey: "Payments", icon: "coins", links: [
        { name: "Run Pay Cycle", desc: "Pay Cycle processing", to: "/soon" },
        { name: "Review Pay Cycle", desc: "Validation before confirming payments", to: "/soon" },
        { name: "Confirm Payments", desc: "Definitive cycle confirmation", to: "/soon" },
        { name: "Reprint Checks", desc: "Re-print payment documents", to: "/soon" },
        { name: "Void Payments", desc: "Cancel checks or transfers", to: "/soon" },
        { name: "Manual Payments", desc: "Register out-of-cycle payments", to: "/soon" },
        { name: "Vendor Prepayments", desc: "Prepayments and invoice application", to: "/soon" },
      ] },
      { titleKey: "Withholding", icon: "tax", links: [
        { name: "Withholding Declaration", desc: "Generate tax certificates (Form 480.6 A/B/C)", to: "/compliance" },
        { name: "Withholding Process", desc: "Calculate and post withholdings", to: "/soon" },
      ] },
      { titleKey: "Reconciliation", icon: "recon", links: [
        { name: "Bank Reconciliation", desc: "Match payments to bank statement", to: "/soon" },
      ] },
      { titleKey: "Reports", icon: "chart", links: [
        { name: "AP Aging", desc: "Overdue debt by vendor and bucket", to: "/soon" },
        { name: "Payment Analysis", desc: "Payments by bank, method, vendor and period", to: "/soon" },
        { name: "Payment Forecast", desc: "Future payment projection", to: "/soon" },
        { name: "Voucher Diary", desc: "Voucher list by period", to: "/soon" },
        { name: "Withholding Report", desc: "Withholding summary for tax filing", to: "/soon" },
      ] },
      { titleKey: "Inquiries", icon: "search", links: [
        { name: "AP Balances Inquiry", desc: "Balance by vendor and BU", to: "/soon" },
        { name: "Vendor Activity", desc: "Transaction history by vendor", to: "/soon" },
      ] },
      { titleKey: "Administration", icon: "shield", links: [
        { name: "Open/Close Periods", desc: "AP accounting period control", to: "/soon" },
        { name: "Batch Processes", desc: "Background process monitoring", to: "/soon" },
      ] },
    ],
  },
  AR: {
    cls: "m-ar", nameKey: "nav.ar",
    sub: "Customers shared with BI, pending items, deposits and application worksheets, aging and dunning. Zero re-entry.",
    cards: [
      { titleKey: "Configuration", icon: "config", links: [
        { name: "AR Business Units", desc: "AR BU setup and configuration", to: "/soon" },
        { name: "AR Options by SetID", desc: "AR global parameters", to: "/soon" },
        { name: "Entry Types", desc: "Item types and accounting behavior", to: "/soon" },
        { name: "Entry Reasons", desc: "Reasons for adjustments, deductions and disputes", to: "/soon" },
        { name: "Deposit Types", desc: "Payment form classification", to: "/soon" },
        { name: "Payment Terms", desc: "Customer payment conditions", to: "/soon" },
        { name: "Aging Buckets", desc: "Aging cycles and ranges", to: "/soon" },
        { name: "Overdue Charges", desc: "Interest rates and rules", to: "/soon" },
        { name: "Dunning Letters", desc: "Collection communication templates", to: "/soon" },
        { name: "Write-off Reasons", desc: "Reasons for account write-off", to: "/soon" },
      ] },
      { titleKey: "Customers", icon: "entities", links: [
        { name: "Add Customer", desc: "Full customer master registration", to: "/entities" },
        { name: "Customer Maintenance", desc: "Edit data, credit and contacts", to: "/entities" },
        { name: "Customer Groups", desc: "Corporate group management", to: "/soon" },
        { name: "Credit Evaluation", desc: "Update limits and credit rating", to: "/soon" },
      ] },
      { titleKey: "Pending Items", icon: "doc", links: [
        { name: "Manual Item Entry", desc: "Register items not coming from BI", to: "/ar" },
        { name: "Batch Item Load", desc: "Bulk import of pending items", to: "/soon" },
        { name: "Post Items", desc: "AR ledger posting process", to: "/soon" },
        { name: "Item Inquiry", desc: "Search and status of open items", to: "/ar" },
      ] },
      { titleKey: "Payments", icon: "coins", links: [
        { name: "Register Deposit", desc: "Bank deposit entry with payments", to: "/ar" },
        { name: "Apply Payments (Worksheet)", desc: "Manual payment-to-item matching", to: "/ar" },
        { name: "Payment Predictor", desc: "Rule-based automatic payment application", to: "/soon" },
        { name: "Unidentified Payments", desc: "Manage payments without customer reference", to: "/soon" },
        { name: "Customer Prepayments", desc: "Prepayments and future application", to: "/soon" },
      ] },
      { titleKey: "Adjustments", icon: "doc", links: [
        { name: "Credit Memos", desc: "Downward adjustments on open items", to: "/soon" },
        { name: "Dispute Management", desc: "Register and track disputed invoices", to: "/soon" },
        { name: "Deduction Management", desc: "Customer deductions on received payments", to: "/soon" },
        { name: "Write-off", desc: "Cancel uncollectible accounts", to: "/entities" },
      ] },
      { titleKey: "Collections", icon: "layers", links: [
        { name: "Collections Workbench", desc: "Consolidated overdue accounts view", to: "/soon" },
        { name: "Generate Statements", desc: "Send statements to customers", to: "/soon" },
        { name: "Dunning Letters", desc: "Generate escalating collection letters", to: "/entities" },
        { name: "Overdue Charges", desc: "Calculate and invoice late interest", to: "/soon" },
      ] },
      { titleKey: "Reports", icon: "chart", links: [
        { name: "AR Aging", desc: "Aging with configurable buckets", to: "/soon" },
        { name: "Collections Diary", desc: "Payments applied in the period", to: "/soon" },
        { name: "Cash Forecast", desc: "Upcoming collections projection", to: "/soon" },
        { name: "Collections Analysis", desc: "Collector effectiveness analysis", to: "/soon" },
        { name: "Period Write-offs", desc: "Written-off accounts with SHA-256 audit trail", to: "/entities" },
        { name: "Allowance CECL / IFRS 9", desc: "Doubtful accts provision · auto-post GL", to: "/entities" },
        { name: "Credit Memos (BI)", desc: "Issued credit memos history from BI", to: "/soon" },
      ] },
      { titleKey: "Inquiries", icon: "search", links: [
        { name: "AR Balances Inquiry", desc: "Balance by customer and BU", to: "/soon" },
        { name: "Customer Activity", desc: "Transaction history by customer", to: "/soon" },
        { name: "Item Inquiry", desc: "Search and status of open items", to: "/ar" },
      ] },
      { titleKey: "Administration", icon: "shield", links: [
        { name: "Open/Close AR Periods", desc: "Accounting period control", to: "/soon" },
        { name: "Batch Processes", desc: "Background process monitoring", to: "/soon" },
      ] },
    ],
  },
  BI: {
    cls: "m-bi", nameKey: "nav.bi",
    sub: "Bill types, charge codes with classified IVU (PR), bill cycles, entry and auto-bill-to-AR. SURI integration ready.",
    cards: [
      { titleKey: "Configuration", icon: "config", links: [
        { name: "BI Business Units", desc: "Billing BU setup and configuration", to: "/soon" },
        { name: "Bill Types", desc: "Invoice category definition", to: "/soon" },
        { name: "Billing Sources", desc: "Origins of billable concepts", to: "/soon" },
        { name: "Billing Cycles", desc: "Invoice generation frequency", to: "/soon" },
        { name: "Billable Concepts", desc: "Charge code catalog · PR IVU classified", to: "/soon" },
        { name: "Payment Terms", desc: "Customer payment conditions", to: "/soon" },
        { name: "Format Options", desc: "Invoice templates and layouts", to: "/soon" },
        { name: "Tax Groups", desc: "VAT and other tax configuration", to: "/soon" },
        { name: "Discounts and Surcharges", desc: "Discount and surcharge types and rates", to: "/soon" },
        { name: "Invoice Identifiers", desc: "Numbering masks", to: "/soon" },
      ] },
      { titleKey: "Customers", icon: "entities", links: [
        { name: "Add Customer", desc: "Master customer registration (shared AR/BI)", to: "/entities" },
        { name: "Customer Maintenance", desc: "Edit existing customers", to: "/entities" },
        { name: "Billing Contacts", desc: "Manage contacts per customer", to: "/soon" },
      ] },
      { titleKey: "Invoices", icon: "doc", links: [
        { name: "New Invoice", desc: "Manual invoice entry · live PR IVU", to: "/bi" },
        { name: "Express Billing", desc: "Simplified invoice entry", to: "/soon" },
        { name: "Concept Load", desc: "Bulk import of billable concepts", to: "/soon" },
        { name: "Approve Invoices", desc: "Invoice approval flow", to: "/soon" },
        { name: "Generate Invoices (Finalize)", desc: "Bulk generation by cycle", to: "/soon" },
        { name: "Print / Send Invoices", desc: "Print and email/portal delivery", to: "/soon" },
        { name: "Cancel Invoices", desc: "Cancellation with credit-memo generation", to: "/soon" },
        { name: "Invoice Inquiry", desc: "Search and invoice detail", to: "/bi" },
      ] },
      { titleKey: "Consolidated Invoices", icon: "layers", links: [
        { name: "Create Consolidated Header", desc: "Group invoices by customer", to: "/soon" },
        { name: "Attach / Detach Invoices", desc: "Consolidated management", to: "/soon" },
      ] },
      { titleKey: "WorkCenter", icon: "search", links: [
        { name: "Billing Panel", desc: "Consolidated pending-work view", to: "/soon" },
      ] },
      { titleKey: "Reports", icon: "chart", links: [
        { name: "Sales Book (VAT / IVU)", desc: "Issued invoices tax register · feeds SC 2918", to: "/compliance" },
        { name: "Revenue Analysis", desc: "Revenue by customer, product, period", to: "/soon" },
        { name: "Pending Invoices", desc: "RDY-status invoices not generated", to: "/soon" },
        { name: "Billing History", desc: "Invoices issued by date range", to: "/soon" },
        { name: "VAT / IVU Analysis", desc: "Billed taxes for filing", to: "/soon" },
      ] },
      { titleKey: "Administration", icon: "shield", links: [
        { name: "Open/Close BI Periods", desc: "Billing period control", to: "/soon" },
        { name: "Batch Processes", desc: "Billing process monitoring", to: "/soon" },
      ] },
    ],
  },
  PO: {
    cls: "m-po", nameKey: "nav.po",
    sub: "Requisitions, purchase orders, receipts, contracts. 3-way match with AP. Integrated Req→PO→Receipt→Voucher flow.",
    cards: [
      { titleKey: "Configuration", icon: "config", links: [
        { name: "PO Business Units", desc: "Purchasing BU setup", to: "/soon" },
        { name: "Purchasing Options", desc: "Global purchasing parameters", to: "/soon" },
        { name: "Document Numbering", desc: "Masks for REQ, PO, RFQ, Contract", to: "/soon" },
        { name: "Approval Rules", desc: "Approval flows and thresholds", to: "/soon" },
        { name: "RFQ Rules", desc: "When quoting is mandatory", to: "/soon" },
        { name: "Document Tolerances", desc: "Tolerances for PO changes", to: "/soon" },
      ] },
      { titleKey: "Items", icon: "entities", links: [
        { name: "Item Catalog", desc: "Master of purchasable items", to: "/soon" },
        { name: "Purchase Categories", desc: "UNSPSC taxonomy", to: "/soon" },
        { name: "Items by Vendor", desc: "Prices and conditions per vendor", to: "/soon" },
        { name: "Purchase Kits", desc: "Item groupings for purchasing", to: "/soon" },
      ] },
      { titleKey: "Requisitions", icon: "doc", links: [
        { name: "New Requisition", desc: "Internal purchase request entry", to: "/soon" },
        { name: "Approve Requisitions", desc: "Requisition approval queue", to: "/soon" },
        { name: "Requisition Sourcing", desc: "REQ-to-PO conversion · consolidate approved reqs", to: "/soon" },
        { name: "Requisition Inquiry", desc: "Search and status of requests", to: "/soon" },
      ] },
      { titleKey: "Quotations (RFQ)", icon: "search", links: [
        { name: "New Quotation", desc: "Create quote request", to: "/soon" },
        { name: "Dispatch RFQ", desc: "Send quote request to vendors", to: "/soon" },
        { name: "Register Responses", desc: "Capture received offers", to: "/soon" },
        { name: "Offer Analysis", desc: "Price and terms comparison", to: "/soon" },
        { name: "Award RFQ", desc: "Winning vendor selection", to: "/soon" },
      ] },
      { titleKey: "Purchase Orders", icon: "po", links: [
        { name: "New Purchase Order", desc: "Direct PO entry", to: "/po" },
        { name: "Approve POs", desc: "PO approval queue", to: "/soon" },
        { name: "Dispatch POs", desc: "Send PO to vendor", to: "/soon" },
        { name: "Modify POs", desc: "Versioned changes", to: "/soon" },
        { name: "Cancel POs", desc: "Full or line-level cancellation", to: "/soon" },
        { name: "PO Inquiry", desc: "Search and status of orders", to: "/po" },
      ] },
      { titleKey: "Receiving", icon: "layers", links: [
        { name: "Register Receipt", desc: "Goods receipt entry", to: "/po" },
        { name: "Return Receipt", desc: "Goods returned to vendor", to: "/soon" },
        { name: "Receipt Inquiry", desc: "Receiving history", to: "/po" },
      ] },
      { titleKey: "Contracts", icon: "doc", links: [
        { name: "New Contract", desc: "Framework contract with vendor", to: "/soon" },
        { name: "Contract Monitoring", desc: "Status, consumption, and expiry", to: "/soon" },
      ] },
      { titleKey: "Budget", icon: "coins", links: [
        { name: "Budget Control", desc: "Pre-commitments and commitments status", to: "/soon" },
      ] },
      { titleKey: "Reports", icon: "chart", links: [
        { name: "Open POs", desc: "Active POs with pending balance", to: "/soon" },
        { name: "Purchase Analysis", desc: "Stats by vendor, category, period", to: "/soon" },
        { name: "PO Aging", desc: "POs without receipt by date bucket", to: "/soon" },
        { name: "Savings Analysis", desc: "Savings vs. list price / budget", to: "/soon" },
        { name: "3-Way Match Audit", desc: "Vouchers with PO_ID · match status · exceptions", to: "/po" },
      ] },
      { titleKey: "Administration", icon: "shield", links: [
        { name: "Budget Control (Rollover)", desc: "Commitment rollover to next period", to: "/soon" },
      ] },
    ],
  },
  EX: {
    cls: "m-ex", nameKey: "nav.ex",
    sub: "Travel Auth, Cash Advance, Expense Report with approval and audit. Integrates GL, AP and corporate card.",
    cards: [
      { titleKey: "My Expenses (Self-Service)", icon: "ex", links: [
        { name: "Create Travel Authorization", desc: "Trip pre-approval with estimates per expense type", to: "/soon" },
        { name: "View my TAs", desc: "Status of own travel authorizations", to: "/soon" },
        { name: "Request Cash Advance", desc: "Cash advance linked to approved TA", to: "/soon" },
        { name: "View my Advances", desc: "Own cash advances with balance", to: "/soon" },
        { name: "Create Expense Report", desc: "ER with expense lines, receipts, policy and submit", to: "/ex" },
        { name: "View my Reports", desc: "Own expense reports and their status", to: "/ex" },
        { name: "My Wallet", desc: "Corporate card and ATM pending assignment", to: "/soon" },
        { name: "Payment History", desc: "Reimbursements received via EFT/check", to: "/soon" },
      ] },
      { titleKey: "Delegated Entry", icon: "entities", links: [
        { name: "TA — Create/Edit", desc: "Authorization entry for another employee", to: "/soon" },
        { name: "CA — Create/Edit", desc: "Advance entry for another employee", to: "/soon" },
        { name: "ER — Create/Edit", desc: "Report entry on behalf of employee", to: "/ex" },
        { name: "Reconcile Advances", desc: "Administrative cash-advance closure", to: "/soon" },
        { name: "Cancel Approved TA", desc: "Cancel trips no longer needed", to: "/soon" },
      ] },
      { titleKey: "Approvals", icon: "lock", links: [
        { name: "Approval Inbox", desc: "Pending TA / CA / ER with Approve/Deny/Send Back/Hold", to: "/ex" },
        { name: "Reassign Work", desc: "Transfer approvals to another approver", to: "/soon" },
        { name: "Summary Approval", desc: "Approve multiple transactions in one action", to: "/soon" },
        { name: "Delegate Approval", desc: "Delegate to backup with expiration", to: "/soon" },
      ] },
      { titleKey: "Accounting", icon: "chart", links: [
        { name: "Accounting Reports", desc: "Status / Employee / Category / Department / Project", to: "/soon" },
        { name: "Adjust Paid (Journal ER)", desc: "Correct post-pay entries", to: "/soon" },
        { name: "Period End Accrual", desc: "Accrual for approved unpaid ERs", to: "/soon" },
        { name: "Audit Routing Templates", desc: "Approval Refinement + Risk Templates", to: "/soon" },
      ] },
      { titleKey: "Processes", icon: "recon", links: [
        { name: "Post Liabilities", desc: "Generates GL entries · processes Journal ER", to: "/soon" },
        { name: "Payment Request to AP", desc: "Creates EMPLOYEE_EXPENSE vouchers staged", to: "/soon" },
        { name: "Process Expenses (Master)", desc: "Validates, posts and submits to pay in one run", to: "/soon" },
        { name: "Batch Approval", desc: "Reports without exceptions auto-approved", to: "/soon" },
        { name: "Import Card Feed", desc: "Amex / Diners / MC / US Bank / Visa → My Wallet", to: "/soon" },
      ] },
      { titleKey: "Configuration", icon: "config", links: [
        { name: "EX Business Units", desc: "Define BU with currency, limits, advances", to: "/soon" },
        { name: "Expense Types", desc: "GL account, class, limits, receipt, duplicates", to: "/soon" },
        { name: "Locations & Rates", desc: "Per diem, hotel, meal by destination", to: "/soon" },
        { name: "Business Purposes", desc: "Allowed list for TA and ER", to: "/soon" },
        { name: "Advance Sources", desc: "Funding source · generates AP payment", to: "/soon" },
        { name: "Employee Profiles", desc: "Banking, card, supervisor, privileges", to: "/soon" },
        { name: "Privilege Templates", desc: "Access to GL/PC ChartFields by role", to: "/soon" },
        { name: "Approval Templates", desc: "Approver Profiles, Refinement, Risk", to: "/soon" },
        { name: "Corporate Cards", desc: "Amex · Diners · MasterCard · US Bank · Visa", to: "/soon" },
      ] },
    ],
  },
};

// The macro module grid — the "select a module" landing.
export function MacroHome() {
  return (
    <div>
      <div className="home-hero">
        <h1>{t("app.title")} — {t("app.system")}</h1>
        <p>{t("home.selectModule")}</p>
      </div>
      <div className="macro-grid">
        {MODULES.map((m) => (
          <Link key={m.code} to={m.to} className={`macro-tile ${m.cls}`}>
            <Icon name={m.icon} />
            <span className="code">{m.code}</span>
            <span className="name">{t(m.nameKey)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// A module's WorkCenter sub-home (dense cards with title+description links).
export function ModuleHub() {
  const { code = "" } = useParams();
  const hub = HUBS[code.toUpperCase()];
  if (!hub) return <MacroHome />;
  const linkTo = (l: HubLink) => (l.to === "/soon" ? `/soon?f=${encodeURIComponent(t(l.name))}` : l.to);
  return (
    <div className={hub.cls}>
      <div className="crumbs"><Link to="/">{t("nav.home")}</Link> › {code.toUpperCase()} · {t(hub.nameKey)}</div>
      <div className="page-head"><h2>{code.toUpperCase()} · {t(hub.nameKey)}</h2></div>
      {hub.sub && <p className="muted hub-sub">{t(hub.sub)}</p>}
      <div className="hub-grid">
        {hub.cards.map((card, i) => (
          <section className="hub-card" key={i}>
            <div className="hub-card__head">
              <span className="hub-card__icon"><Icon name={card.icon} /></span>
              <span className="hub-card__title">{t(card.titleKey)}</span>
            </div>
            <ul className="hub-links">
              {card.links.map((l, j) => (
                <li key={j}>
                  <NavLink to={linkTo(l)} className="hub-link">
                    <span className="ln">{t(l.name)}</span>
                    <span className="ld">{t(l.desc)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

// Placeholder for legacy functions not yet wired in the Go rewrite.
export function SoonPage() {
  const [sp] = useSearchParams();
  const fn = sp.get("f") || "";
  return (
    <div>
      <div className="crumbs"><Link to="/">{t("nav.home")}</Link> › {fn}</div>
      <div className="page-head"><h2>{fn || t("soon.title")}</h2></div>
      <div className="empty">{t("soon.body")}</div>
    </div>
  );
}
